import { Component, OnInit } from '@angular/core';
import { ColorMapper } from '../../services/color-mapper';
import { map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { AppService } from 'src/app/app.service';
import { Brand } from 'src/app/components/company/models/new/brand.model';
import { Category } from 'src/app/components/company/models/new/category';
import { Template } from 'src/app/components/company/models/new/template';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { TemplateMapper } from '../../services/template-mapper';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { BrandFilterService } from 'src/app/services/filters/brand-filter.service';
import { CategoryFilterService } from 'src/app/services/filters/category-filter.service';
import { Color, Style } from '../../models/new/style';
import * as _ from 'lodash';
import { AddShotModalComponent } from './add-shot-modal/add-shot-modal.component';
import { AspectRatio, MediaFormat, Shot, ShotType } from '../../models/new/shot';
import { StyleFilterService } from 'src/app/services/filters/style-filter.service';
import { ColorFilterService } from 'src/app/services/filters/color-filter.service';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import { Collection } from '../../models/new/collection';
import { Gender } from '../../models/new/style';
import { StyleGroup } from '../../models/new/style-group';
import { CollectionFilterService } from 'src/app/services/filters/collection-filter.service';
import { GenderFilterService } from 'src/app/services/filters/gender-filter.service';
import { StyleGroupFilterService } from 'src/app/services/filters/style-group-filter.service';

@Component({
	selector: 'bt-company-template-detail',
	standalone: false,
	templateUrl: './company-template-detail.component.html',
	styleUrl: './company-template-detail.component.scss'
})
export class CompanyTemplateDetailComponent implements OnInit {
	loading: boolean = false;
	currentWarehouse: Warehouse | null = null;
	template: Template | null = null;
	colors: Color[] = [];
	isNew: boolean = false;

	constructor(
		private graphQLService: GraphQLService,
		private activatedRoute: ActivatedRoute,
		private templateMapper: TemplateMapper,
		private appService: AppService,
		private utilitiesService: UtilitiesService,
		private dialog: MatDialog,
		private brandFilterService: BrandFilterService,
		private categoryFilterService: CategoryFilterService,
		private collectionFilterService: CollectionFilterService,
		private genderFilterService: GenderFilterService,
		private styleGroupFilterService: StyleGroupFilterService,
		private styleFilterService: StyleFilterService,
		private colorFilterService: ColorFilterService,
		private colorMapper: ColorMapper,
		private router: Router
	) {}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe((params) => {
			const templateId = params['templateId'];

			if (templateId != 'new') {
				this.getTemplate(Number(templateId));
			} else {
				this.isNew = true;
				this.template = new Template();
				this.template.shots = [];
				this.template.styles = [];
			}
		});
	}

	onBack(): void {
		this.router.navigate(['/company/template-list']);
	}

	canSelectCollectionAndCategory(): boolean {
		return !!this.template?.brand;
	}

	hasCollectionOrCategorySelection(): boolean {
		return !!this.template?.category || !!this.template?.collection;
	}

	canSelectGenderStyleGroupAndStyle(): boolean {
		return this.canSelectCollectionAndCategory();
	}

	canSelectColors(): boolean {
		return !!this.template?.styles?.length && this.template.styles.length === 1;
	}

	private resetStylesAndColors(): void {
		if (!this.template) return;

		this.template.styles = [];
		this.template.colors = [];
		this.colors = [];
	}

	private resetDeepScopeFilters(): void {
		if (!this.template) return;

		this.template.gender = undefined;
		this.template.styleGroup = undefined;
		this.resetStylesAndColors();
	}

	private enforceScopeHierarchy(): void {
		if (!this.template) return;

		if (!this.template.brand) {
			this.template.collection = undefined;
			this.template.category = undefined;
			this.resetDeepScopeFilters();
		}
	}

	getTemplate(templateId: number) {
		this.graphQLService
			.getData<Template>(TemplatesQueries.GET_TEMPLATES, { where: { templateId: { eq: templateId } }, first: 1 })
			.pipe(map((res: any) => this.templateMapper.toTemplate(res.data.templates.nodes[0])))
			.subscribe((data: Template) => {
				this.template = data;
				this.colors = data.colors ? data.colors.map((c) => this.colorMapper.toColor(c)) : [];
				console.log('template', this.template);
			});
	}

	saveTemplate() {
		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Save',
					message: 'Are you sure you want to save the changes to this template?'
				}
			})
			.afterClosed()
			.subscribe((confirm: boolean) => {
				if (confirm) {
					if (this.template) {
						this.graphQLService.postData(MutationQuery.CREATE_TEMPLATE, { createTemplateInput: this.templateMapper.toTemplateQL(this.template) }).subscribe({
							next: () => {
								this.utilitiesService.showSnackbar('Template saved successfully', SnackbarType.Success);
							},
							error: (err: any) => {
								try {
									console.log(err);
									this.utilitiesService.showSnackbar(err.errors[0].extensions.message, SnackbarType.Error);
								} catch (error) {
									this.utilitiesService.showSnackbar('An error occurred while saving the template', SnackbarType.Error);
								}
							}
						});
					}
				}
			});
	}

	openBrandFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<Brand>, {
					data: {
						title: 'Brand',
						singleSelection: true,
						selected: this.template.brand
							? [
									{
										...this.template?.brand,
										displayId: this.template.brand.id,
										displayName: this.template.brand.name,
										selected: true
									}
								]
							: [],
						filterService: this.brandFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Array<Selectable<Brand>>) => {
						if (res) {
							if (this.template) {
								this.template.brand = res.length ? res[0] : undefined;
								this.template.collection = undefined;
								this.template.category = undefined;
								this.resetDeepScopeFilters();
								this.enforceScopeHierarchy();
							}
						}
					}
				});
		}
	}

	openCollectionFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<Collection>, {
					data: {
						title: 'Collection',
						singleSelection: true,
						selected: this.template.collection
							? [
									{
										...this.template.collection,
										displayId: this.template.collection.id,
										displayName: this.template.collection.name,
										selected: true
									}
								]
							: [],
						filterService: this.collectionFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Array<Selectable<Collection>>) => {
						if (res && this.template) {
							this.template.collection = res.length
								? {
										id: res[0].displayId,
										name: res[0].displayName,
										startDate: res[0].startDate,
										endDate: res[0].endDate
									}
								: undefined;
							this.resetStylesAndColors();
							this.enforceScopeHierarchy();
						}
					}
				});
		}
	}

	openCategoryFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<Category>, {
					data: {
						title: 'Category',
						singleSelection: true,
						selected: this.template.category
							? [
									{
										...this.template?.category,
										displayId: this.template.category.id,
										displayName: this.template.category.name,
										selected: true
									}
								]
							: [],
						filterService: this.categoryFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Array<Selectable<Category>>) => {
						if (res) {
							if (this.template) {
								this.template.category = res.length ? res[0] : undefined;
								this.resetStylesAndColors();
								this.enforceScopeHierarchy();
							}
						}
					}
				});
		}
	}

	openGenderFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<Gender>, {
					data: {
						title: 'Gender',
						singleSelection: true,
						selected: this.template.gender
							? [
									{
										...this.template.gender,
										displayId: this.template.gender.id,
										displayName: this.template.gender.name,
										selected: true
									}
								]
							: [],
						filterService: this.genderFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Array<Selectable<Gender>>) => {
						if (res && this.template) {
							this.template.gender = res.length ? { id: res[0].displayId, name: res[0].displayName } : undefined;
							this.resetStylesAndColors();
						}
					}
				});
		}
	}

	openStyleGroupFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<StyleGroup>, {
					data: {
						title: 'Style Group',
						singleSelection: true,
						selected: this.template.styleGroup
							? [
									{
										...this.template.styleGroup,
										displayId: this.template.styleGroup.id,
										displayName: this.template.styleGroup.name,
										selected: true
									}
								]
							: [],
						filterService: this.styleGroupFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Array<Selectable<StyleGroup>>) => {
						if (res && this.template) {
							this.template.styleGroup = res.length ? { id: res[0].displayId, name: res[0].displayName } : undefined;
							this.resetStylesAndColors();
						}
					}
				});
		}
	}

	openStylesFilterModal(): void {
		if (this.template) {
			this.dialog
				.open(FilterListComponent<Style>, {
					data: {
						title: 'Styles',

						selected: this.template.styles
							? this.template.styles.map((style) => ({
									...style,
									displayId: style.modelId,
									displayName: style.code + ' - ' + style.name,
									selected: true
								}))
							: [],
						variables: {
							brandIds: this.template.brand?.id ? [this.template.brand.id] : undefined,
							categoryIds: this.template.category?.id ? [this.template.category.id] : undefined,
							collectionIds: this.template.collection?.id ? [this.template.collection.id] : undefined,
							genderIds: this.template.gender?.id ? [this.template.gender.id] : undefined,
							styleGroupIds: this.template.styleGroup?.id ? [this.template.styleGroup.id] : undefined
						},
						filterService: this.styleFilterService
					},
					width: '400px'
				})
				.afterClosed()
				.subscribe({
					next: (res: Selectable<Style>[]) => {
						if (res) {
							if (this.template) this.template.styles = res.length ? res.map((style) => ({ ...style })) : [];

							this.colors = [];
						}
					}
				});
		}
	}

	openColorsFilterModal(): void {
		if (!this.template || !this.template.styles || !this.template.styles.length) return;

		let style = this.template.styles[0];

		this.dialog
			.open(FilterListComponent<Color>, {
				data: {
					title: 'Colors',
					selected:
						style.colors && style.colors.length
							? style.colors.map((color) => ({
									...color,
									displayId: color.id,
									displayName: `${color.code} - ${color.name}`,
									selected: true
								}))
							: [],
					variables: { modelId: style.modelId },
					filterService: this.colorFilterService
				}
			})
			.afterClosed()
			.subscribe({
				next: (res: Selectable<Color>[]) => {
					if (res) {
						console.log(res);
						this.colors = res.map((color) => ({ ...color }));
						style.colors = _.cloneDeep(res);
					}
				}
			});
	}

	openAddShotModal() {
		this.dialog
			.open(AddShotModalComponent, {
				data: {
					shots: this.template?.shots || []
				}
			})
			.afterClosed()
			.subscribe((result: Shot) => {
				if (result) {
					if (this.template && this.template.shots) this.template.shots.push(result);

					console.log(result);
				}
			});
	}

	removeShot(index: number) {
		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Remove shot',
					message: 'Are you sure you want to remove this shot?'
				}
			})
			.afterClosed()
			.subscribe((confirm: boolean) => {
				if (confirm) {
					if (this.template && this.template.shots) this.template.shots.splice(index, 1);
					this.utilitiesService.showSnackbar('Shot removed successfully', SnackbarType.Success);
				}
			});
	}
}
